import styled from '@emotion/styled';
import React from 'react';
import { Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { GoalLabel } from './GoalLabel';
import { GoalLabelWithTooltip } from './GoalLabelWithTooltip';
import { ClickableLabel } from './ClickableLabel';

type Props = {
  goalId: string;
  achievementScaleId?: string | null;
  achievementScaleName?: string | null;
  achievementScore?: number | null;
  finalWeight?: number | null;
  weightedAchievementScore?: number | null;
  displayResult?: string | null;
  onClick?: () => void;
  canEditResult?: boolean;
};

const i18WeightedScore = 'performanceReview.goals2.weightedScore.';

const formatScore = (value: number | null | undefined): string =>
  value != null ? String(value) : '-';

export const GoalResultScaleLabel: React.FC<React.PropsWithChildren<Props>> = ({
  goalId,
  achievementScaleId,
  achievementScaleName,
  achievementScore,
  finalWeight,
  weightedAchievementScore,
  displayResult,
  onClick,
  canEditResult,
}) => {
  const { t } = useTranslation();

  const disabledHint =
    canEditResult === false
      ? t(i18WeightedScore + 'resultUpdateNotAvailable')
      : undefined;

  if (!achievementScaleId) {
    return (
      <Container>
        <ClickableLabel onClick={canEditResult !== false ? onClick : undefined}>
          {disabledHint ? (
            <GoalLabelWithTooltip
              icon="chart-bar-outline"
              label={`${t(i18WeightedScore + 'result')}: ${
                displayResult ?? '-'
              }`}
              className="me-3 text-muted"
              tooltip={
                <Tooltip id={`result-disabled-tooltip-${goalId}`}>
                  {disabledHint}
                </Tooltip>
              }
            />
          ) : (
            <GoalLabel
              icon="chart-bar-outline"
              label={`${t(i18WeightedScore + 'result')}: ${
                displayResult ?? '-'
              }`}
              className="me-3"
            />
          )}
        </ClickableLabel>
      </Container>
    );
  }

  const hasWeight = finalWeight != null;
  const hasResult = displayResult != null && displayResult !== '';

  return (
    <Container>
      <ClickableLabel onClick={canEditResult !== false ? onClick : undefined}>
        <GoalLabelWithTooltip
          icon="chart-bar-outline"
          label={`${t(i18WeightedScore + 'score')}: ${
            achievementScore != null
              ? `${formatScore(achievementScore)} (${formatScore(
                  weightedAchievementScore,
                )})`
              : '-'
          }`}
          className={canEditResult === false ? 'me-3 text-muted' : 'me-3'}
          tooltip={
            <StyledTooltip id={`weighted-score-tooltip-${goalId}`}>
              <ul className="list-unstyled text-start mb-0">
                {disabledHint && (
                  <li className="mb-1 fw-bold">{disabledHint}</li>
                )}
                <li>
                  {t(i18WeightedScore + 'result')}: {displayResult ?? '-'}
                </li>
                <li>
                  {t(i18WeightedScore + 'score')}:{' '}
                  {hasResult ? formatScore(achievementScore) : '-'}
                </li>
                {hasWeight && (
                  <li>
                    {t(i18WeightedScore + 'weightedFinalScore')}:{' '}
                    {hasResult ? formatScore(weightedAchievementScore) : '-'} (
                    {t(i18WeightedScore + 'finalWeight')}:{' '}
                    {formatScore(finalWeight)})
                  </li>
                )}
                {achievementScaleName && (
                  <li>
                    {t(i18WeightedScore + 'achievementScale')}:{' '}
                    {achievementScaleName}
                  </li>
                )}
              </ul>
            </StyledTooltip>
          }
        />
      </ClickableLabel>
    </Container>
  );
};

const Container = styled('span')({
  '& button': {
    color: 'inherit !important',
  },
});

const StyledTooltip = styled(Tooltip)({
  '& .tooltip-inner': {
    maxWidth: 'none',
    whiteSpace: 'nowrap',
  },
});
